/**
 * FoliarSyncBanner — shared offline/pending-sync banner for the two FOLIAR
 * operator screens (NutritionMySessionsPage, NutritionFoliarFieldPage).
 *
 * Extracted (F5 Task 9, review round 1) because the presentation layer
 * (resolveSyncBanner's pure result -> Notice tone/text) had been copy-pasted
 * between the two pages. That is precisely the decay path foliarOperator.ts's
 * own header comment warns has already bitten this initiative twice — a
 * second copy that diverges on a reachable case. The PURE resolver
 * (resolveSyncBanner) already lived in foliarOperator.ts; this component is
 * the presentation half of the same "operator's two screens agree" contract.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Notice } from '../patterns';
import { resolveSyncBanner } from './foliarOperator';

interface FoliarSyncBannerProps {
  online: boolean;
  pendingCount: number;
  /**
   * Optional spacing utility (e.g. "mb-3"), forwarded to Notice. Omitted by
   * NutritionMySessionsPage (its original banner carried none, and this
   * component must render byte-identically there — its 15 tests are
   * unedited); NutritionFoliarFieldPage passes "mb-3" for its own layout.
   */
  className?: string;
}

const FoliarSyncBanner: React.FC<FoliarSyncBannerProps> = ({ online, pendingCount, className }) => {
  const { t } = useTranslation();
  const banner = resolveSyncBanner(online, pendingCount);

  if (banner.kind === 'none') return null;

  const bannerText =
    banner.kind === 'offlineIdle' ? t('nutrition.mySessions.syncOfflineIdle')
      : banner.kind === 'offlineQueued' ? t('nutrition.mySessions.syncOfflineQueued', { count: banner.count })
        : t('nutrition.mySessions.syncOnlineQueued', { count: banner.count });

  return (
    <Notice
      tone={banner.kind === 'offlineIdle' ? 'info' : 'warn'}
      testId="foliar-sync-banner"
      className={className}
    >
      {bannerText}
    </Notice>
  );
};

export default FoliarSyncBanner;
